import { createAction, props } from '@ngrx/store';
import { IDjangoPaginationData } from '@shared/ui-components/au-paginator/pagination-page.info';
import { AuGqlError } from '@app/shared';
import { EntityMap, EntityMapOne, Update } from '@ngrx/entity';
import {
	ICmmsAsset,
	ICmmsAssetCreateVariables,
	ICmmsAssetEditPayload,
	ICmmsAssetEntityToAttach,
	ICmmsAssetQueryVariables,
	ILinkAssetsObjectsVariables,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';

export const startLoadingCmmsAssets = createAction(
	'[CMMS Assets] Start loading assets',
	props<{
		variables: ICmmsAssetQueryVariables;
	}>()
);

export const reloadCmmsAssetsWithSavePagination = createAction(
	'[CMMS Assets] reload assets in table with save pagination'
);

export const cmmsAssetsFailedToLoad = createAction('[CMMS Assets] assets failed to load');

export const cmmsAssetsLoaded = createAction(
	'[CMMS Assets] assets loaded',
	props<{
		assetPagination: IDjangoPaginationData;
		assets: ICmmsAsset[];
	}>()
);

export const storeCmmsAssetsQueryVariables = createAction(
	'[CMMS Assets] Store assets query variables',
	props<{ variables: ICmmsAssetQueryVariables }>()
);

export const dropCmmsAssetsQueryVariables = createAction(
	'[CMMS Assets] Drop assets query variables'
);

export const setCmmsAssetForEditing = createAction(
	'[CMMS Assets] Add asset for editing',
	props<{
		assetEdit: Partial<ICmmsAsset>;
	}>()
);
export const setEditAssetPkAndForceReload = createAction(
	'[CMMS Assets] Add asset for editing and turn on force reload',
	props<{
		assetEdit: Partial<ICmmsAsset>;
	}>()
);
export const turnOffForceReload = createAction('[CMMS Assets] turn off force reload');
export const setCmmsAssets = createAction(
	'[CMMS Assets] Set assets',
	props<{ assets: ICmmsAsset[] }>()
);
export const startCreateCmmsAsset = createAction(
	'[CMMS Assets] Start create asset',
	props<{
		assets: Partial<ICmmsAssetCreateVariables>[];
		entityToAttach?: ICmmsAssetEntityToAttach;
		skipTableReload?: boolean;
	}>()
);
export const createCmmsAssetSuccess = createAction(
	'[CMMS Assets] Create asset success',
	props<{ assets?: ICmmsAsset[]; skipTableReload?: boolean }>()
);
export const createCmmsAssetError = createAction(
	'[CMMS Assets] Create asset error',
	props<{ error?: AuGqlError }>()
);
export const addCmmsAsset = createAction(
	'[CMMS Assets] Add asset',
	props<{ asset: ICmmsAsset }>()
);
export const setCmmsAsset = createAction(
	'[CMMS Assets] Set asset',
	props<{ asset: ICmmsAsset }>()
);
export const setCmmsAssetForEdit = createAction(
	'[CMMS Assets] Set asset for edit',
	props<{ asset: ICmmsAsset }>()
);
// update one or insert one asset
export const upsertCmmsAsset = createAction(
	'[CMMS Assets] Upsert asset',
	props<{ asset: ICmmsAsset }>()
);
export const addCmmsAssets = createAction(
	'[CMMS Assets] Add assets',
	props<{ assets: ICmmsAsset[] }>()
);
// update one or insert one asset array
export const upsertCmmsAssets = createAction(
	'[CMMS Assets] Upsert assets',
	props<{ assets: ICmmsAsset[] }>()
);
export const startUpdateCmmsAsset = createAction(
	'[CMMS Assets] Start update asset',
	props<{ asset: ICmmsAssetEditPayload; skipTableReload?: boolean }>()
);
export const updateCmmsAssetSuccess = createAction(
	'[CMMS Assets] Update asset success',
	props<{ asset: ICmmsAsset; skipTableReload?: boolean }>()
);

export const startLinkingCmmsAsset = createAction(
	'[CMMS Assets] Start Mapping asset',
	props<ILinkAssetsObjectsVariables>()
);

export const linkingCmmsAssetSuccess = createAction(
	'[CMMS Assets] Mapping asset success',
	props<{ assetId: string }>()
);
export const updateCmmsAsset = createAction(
	'[CMMS Assets] Update asset in state',
	props<{ update: Update<ICmmsAsset> }>()
);
export const updateCmmsAssets = createAction(
	'[CMMS Assets] Update assets',
	props<{ updates: Update<ICmmsAsset>[] }>()
);
export const mapCmmsAsset = createAction(
	'[CMMS Assets] Map asset',
	props<{ entityMap: EntityMapOne<ICmmsAsset> }>()
);
export const mapCmmsAssets = createAction(
	'[CMMS Assets] Map assets',
	props<{ entityMap: EntityMap<ICmmsAsset> }>()
);
export const startDeletingCmmsAsset = createAction(
	'[CMMS Assets] Start deleting asset',
	props<{ pk: string; skipTableReload?: boolean }>()
);
export const deletingCmmsAssetSuccess = createAction(
	'[CMMS Assets] Deleting asset success',
	props<{ pk: string; skipTableReload?: boolean }>()
);
export const deleteCmmsAssetFromState = createAction(
	'[CMMS Assets] Delete asset from state',
	props<{ pk: string }>()
);
export const deleteCmmsAssets = createAction(
	'[CMMS Assets] Delete assets',
	props<{ pks: string[] }>()
);
export const openAssetWorkspaceFromOutside = createAction(
	'[CMMS Assets] Open asset workspace from outside',
	props<{ pk: string }>()
);
export const openAssetWorkspaceFromOutsideByGlobalId = createAction(
	'[CMMS Assets] Open asset workspace from outside by global id',
	props<{ globalId: string }>()
);
export const openNewAssetWorkspaceFromOutside = createAction(
	'[CMMS Assets] Open new asset workspace from outside',
	(assetData = null) => ({ assetData })
);
export const setAssetOutsideFlagForWorkspace = createAction(
	'[CMMS Assets] set outside flag for asset work space',
	props<{ fromOutside: boolean }>()
);

export const setAssetsTableConfig = createAction(
	'[CMMS Assets] Set assets table config',
	props<{
		isExportable: boolean;
		columns: ICMMSColumn[];
	}>()
);

export const clearAssetExternalData = createAction(
	'[CMMS Assets] Clear asset external data'
);

export const addAssetExternalData = createAction(
	'[CMMS Assets] Add asset external data',
	(assetData = null) => ({ assetData })
);
